import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CartContext } from '../context/CartContext';

const DEFAULT_BOOKING_TIME = '09:00';

const TIME_OPTIONS = Array.from({ length: 33 }, (_, index) => {
  const totalMinutes = 6 * 60 + index * 30;
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
  const minutes = String(totalMinutes % 60).padStart(2, '0');
  const value = `${hours}:${minutes}`;
  const labelDate = new Date(`1970-01-01T${value}:00`);
  return { value, label: labelDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) };
});

const buildInitialForm = ({ pooja, tier, editItem }) => {
  const requiredItems = editItem?.requiredItems || pooja?.itemsRequired || [];
  const selectedRequiredItems = editItem?.punditBringItems || [];
  return {
    bookingDate: editItem?.bookingDate || '',
    bookingTime: editItem?.bookingTime || DEFAULT_BOOKING_TIME,
    punditBringItems: selectedRequiredItems,
    requiredItems,
    needsPrasadam: editItem?.needsPrasadam || false,
    prasadamSelections: editItem?.prasadamSelections || [],
    addonSelections: editItem?.addonSelections || [],
    tier: editItem?.selectedTier || tier || null,
  };
};

const PoojaAddonsModal = ({ open, onClose, pooja, tier, images = [], editItem = null }) => {
  const { addToCart, updateCartItem, setIsOpen } = useContext(CartContext);
  const [prasadamItems, setPrasadamItems] = useState([]);
  const [addonItems, setAddonItems] = useState([]);
  const [checkoutSettings, setCheckoutSettings] = useState({ paymentGatewayEnabled: false });
  const [form, setForm] = useState(() => buildInitialForm({ pooja, tier, editItem }));
  const isEditing = Boolean(editItem);

  useEffect(() => {
    if (!open) return;
    setForm(buildInitialForm({ pooja, tier, editItem }));
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/prasadam`)
      .then((res) => setPrasadamItems(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error('Unable to load prasadam items', err);
        setPrasadamItems([]);
      });
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/addons`)
      .then((res) => setAddonItems(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error('Unable to load pooja addons', err);
        setAddonItems([]);
      });
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/checkout-settings`)
      .then((res) => setCheckoutSettings({
        paymentGatewayEnabled: Boolean(res.data?.paymentGatewayEnabled),
      }))
      .catch((err) => {
        console.error('Unable to load checkout settings', err);
        setCheckoutSettings({ paymentGatewayEnabled: false });
      });
  }, [open, pooja, tier, editItem]);

  const prasadamSelections = useMemo(() => (
    prasadamItems.map((item) => ({
      ...item,
      quantity: form.prasadamSelections.find((selected) => selected.id === item.id)?.quantity || 0,
    }))
  ), [prasadamItems, form.prasadamSelections]);

  const addonSelections = useMemo(() => (
    addonItems.map((item) => ({
      ...item,
      quantity: form.addonSelections.find((selected) => selected.id === item.id)?.quantity || 0,
    }))
  ), [addonItems, form.addonSelections]);

  const prasadamTotal = prasadamSelections.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const addonTotal = addonSelections.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const selectedTier = form.tier || tier;
  const originalBasePrice = Number(selectedTier?.originalPrice || editItem?.originalBasePrice || selectedTier?.price || editItem?.basePrice || 0);
  const saleBasePrice = Number(selectedTier?.price || editItem?.basePrice || 0);
  const basePrice = checkoutSettings.paymentGatewayEnabled ? saleBasePrice : 0;
  const finalPrice = basePrice + prasadamTotal + addonTotal;

  if (!open) return null;

  const toggleRequiredItem = (item) => {
    setForm((prev) => {
      const exists = prev.punditBringItems.includes(item);
      return {
        ...prev,
        punditBringItems: exists
          ? prev.punditBringItems.filter((selected) => selected !== item)
          : [...prev.punditBringItems, item],
      };
    });
  };

  const updatePrasadamQuantity = (item, change) => {
    setForm((prev) => {
      const existing = prev.prasadamSelections.find((selected) => selected.id === item.id);
      const quantity = Math.max((existing?.quantity || 0) + change, 0);
      const withoutItem = prev.prasadamSelections.filter((selected) => selected.id !== item.id);
      return {
        ...prev,
        prasadamSelections: quantity > 0
          ? [...withoutItem, { id: item.id, name: item.name, price: item.price, quantity }]
          : withoutItem,
      };
    });
  };

  const updateAddonQuantity = (item, change) => {
    setForm((prev) => {
      const existing = prev.addonSelections.find((selected) => selected.id === item.id);
      const quantity = Math.max((existing?.quantity || 0) + change, 0);
      const withoutItem = prev.addonSelections.filter((selected) => selected.id !== item.id);
      return {
        ...prev,
        addonSelections: quantity > 0
          ? [...withoutItem, {
            id: item.id,
            name: item.name,
            price: item.price,
            unitQuantity: item.unitQuantity,
            unitLabel: item.unitLabel,
            quantity,
          }]
          : withoutItem,
      };
    });
  };

  const saveSelection = () => {
    if (!form.bookingDate || !form.bookingTime) {
      alert('Please select date and time for this pooja.');
      return;
    }

    const requiredItems = form.requiredItems || [];
    const userArrangedItems = requiredItems.filter((item) => !form.punditBringItems.includes(item));
    const configuredItem = {
      ...(editItem || pooja),
      id: editItem?.id || `${pooja.id}-${(form.tier || tier)?.id || 'tier'}-${Date.now()}`,
      poojaId: pooja?.id || editItem?.poojaId,
      tierId: (form.tier || tier)?.id || editItem?.tierId,
      selectedTier: form.tier || tier || editItem?.selectedTier,
      name: editItem?.name || `${pooja.name} (${(form.tier || tier)?.duration || 'Selected'})`,
      type: 'Pooja',
      image_url: editItem?.image_url || images[0],
      basePrice,
      originalBasePrice,
      price: finalPrice,
      bookingDate: form.bookingDate,
      bookingTime: form.bookingTime,
      requiredItems,
      punditBringItems: form.punditBringItems,
      userArrangedItems,
      needsPrasadam: prasadamSelections.some((item) => item.quantity > 0),
      prasadamSelections: prasadamSelections
        .filter((item) => item.quantity > 0)
        .map(({ id, name, price, quantity }) => ({ id, name, price, quantity })),
      prasadamTotal,
      addonSelections: addonSelections
        .filter((item) => item.quantity > 0)
        .map(({ id, name, price, unitQuantity, unitLabel, quantity }) => ({ id, name, price, unitQuantity, unitLabel, quantity })),
      addonTotal,
      paymentGatewayEnabled: checkoutSettings.paymentGatewayEnabled,
      itemTotal: finalPrice,
      qty: 1,
    };

    if (isEditing) {
      updateCartItem(editItem.id, configuredItem);
    } else {
      addToCart(configuredItem);
    }
    onClose?.();
    setIsOpen(true);
  };

  return (
    <div className="modal-overlay pooja-addons-overlay">
      <div className="pooja-addons-modal">
        <div className="subscription-modal-header pooja-config-header">
          <div>
            <span className="pooja-config-eyebrow">Personalize your booking</span>
            <h3>{isEditing ? 'Modify pooja details' : 'Complete pooja details'}</h3>
            <p className="subscription-current">{pooja?.name || editItem?.name}</p>
          </div>
          <button className="close-drawer" onClick={onClose}>Close</button>
        </div>

        <div className="pooja-config-body">
          <div className="pooja-config-section">
            <div className="pooja-config-section-title">
              <span>01</span>
              <div>
                <h4>Schedule</h4>
                <p>Choose when the ritual should be performed.</p>
              </div>
            </div>
            <div className="form-row">
            <div className="input-group">
              <label>Date</label>
              <input type="date" value={form.bookingDate} onChange={(e) => setForm({ ...form, bookingDate: e.target.value, bookingTime: form.bookingTime || DEFAULT_BOOKING_TIME })} />
            </div>
            <div className="input-group">
              <label>Time</label>
              <select value={form.bookingTime} onChange={(e) => setForm({ ...form, bookingTime: e.target.value })}>
                {!TIME_OPTIONS.some((option) => option.value === form.bookingTime) && form.bookingTime && (
                  <option value={form.bookingTime}>{form.bookingTime}</option>
                )}
                {TIME_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            </div>
            <p className="muted-copy schedule-hint">A suggested 9:00 AM time is selected automatically; you can adjust it for your preferred muhurta.</p>
          </div>

          <div className="pooja-addons-list pooja-config-section">
            <div className="pooja-config-section-title">
              <span>02</span>
              <div>
                <h4>Items pundit should bring</h4>
                <p>Pick the items you want the pundit to arrange.</p>
              </div>
            </div>
            {(form.requiredItems || []).length === 0 && <p className="muted-copy">No required items listed for this pooja.</p>}
            <div className="pooja-config-check-grid">
              {(form.requiredItems || []).map((item) => (
                <label className="auth-checkbox pooja-config-check" key={item}>
                  <input
                    type="checkbox"
                    checked={form.punditBringItems.includes(item)}
                    onChange={() => toggleRequiredItem(item)}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
            <small className="muted-copy">Unchecked items will be arranged by the user.</small>
          </div>

          <div className="pooja-addons-list pooja-config-section highlighted">
            <div className="pooja-config-section-title">
              <span>03</span>
              <div>
                <h4>Prasadam</h4>
                <p>Order fresh prasadam from us and enjoy a hassle-free pooja preparation.</p>
              </div>
            </div>
            {prasadamSelections.map((item) => (
              <div className="pooja-addon-card" key={item.id}>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.description}</span>
                </div>
                <div className="pooja-addon-price">${item.price}</div>
                <div className="qty-selector premium">
                  <button type="button" onClick={() => updatePrasadamQuantity(item, -1)}>-</button>
                  <span>{item.quantity}</span>
                  <button type="button" onClick={() => updatePrasadamQuantity(item, 1)}>+</button>
                </div>
              </div>
            ))}
            {prasadamSelections.length === 0 && <p className="muted-copy">Prasadam options will appear here once admin adds them.</p>}
          </div>

          {addonSelections.length > 0 && (
            <div className="pooja-addons-list pooja-config-section">
              <div className="pooja-config-section-title">
                <span>04</span>
                <div>
                  <h4>Add-ons</h4>
                  <p>Add any extra pooja essentials you would like delivered.</p>
                </div>
              </div>
              {addonSelections.map((item) => (
                <div className="pooja-addon-card" key={item.id}>
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.unitQuantity} {item.unitLabel}</span>
                  </div>
                  <div className="pooja-addon-price">${item.price}</div>
                  <div className="qty-selector premium">
                    <button type="button" onClick={() => updateAddonQuantity(item, -1)}>-</button>
                    <span>{item.quantity}</span>
                    <button type="button" onClick={() => updateAddonQuantity(item, 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pooja-addons-actions">
          <div className="price-wrapper">
            {originalBasePrice > saleBasePrice && (
              <span className="struck-price">${originalBasePrice.toFixed(2)}</span>
            )}
            <p className="price">{checkoutSettings.paymentGatewayEnabled ? `$${finalPrice.toFixed(2)}` : 'Free booking today'}</p>
            {originalBasePrice > saleBasePrice && <small className="discount-badge">{selectedTier?.discountLabel || 'Limited-time discount'}</small>}
          </div>
          <button type="button" className="pp-secondary-action" onClick={onClose}>Cancel</button>
          <button type="button" className="login-submit-btn" onClick={saveSelection}>
            {isEditing ? 'Update Cart' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoojaAddonsModal;
